/**
 * 1.5.0 shot detection: the contracts both streams share (docs/dev/shots-1.5.0-build.md, plan
 * ~/.claude/plans/prism-shot-detection.md). Step 0's file: S1 and S2 read it and send changes to the merger.
 *
 * Shots are detected on the proxy (progressive, constant frame rate, zero-based), so their times are already segment
 * ticks. The sprite (scrub images) is made from each shot's first frame plus, inside long shots, a forced tile every
 * keyframe interval; forced tiles never make shots. The read-only system layer `s_shots` holds one empty segment per
 * shot (migration 0098). Log layers can be filled from the shots (one empty segment per shot, `source: 'shots'`).
 */
import { z } from 'zod';
import { framesToTicks, rationalToNumber, secondsToTicks, ticksToSeconds, TICKS_PER_SECOND, type Rational } from './time.ts';
import type { SpriteLayout } from './assets.ts';

// ---------------------------------------------------------------- the system layer

/** The read-only Shots layer (0098, ensureShotsStratum): system, not searchable, after QC (position 1001). */
export const SHOTS_STRATUM_ID = 's_shots';
export const SHOTS_STRATUM_LABEL = 'Shots';
/** Muted grey-blue, so the shots stay in the background of the lanes. */
export const SHOTS_STRATUM_COLOR = '#7c8799';
export const SHOTS_STRATUM_POSITION = 1001;
/** timeline_segments.source of a segment made by Fill from shots (and of the Shots layer's own segments). */
export const SEGMENT_SOURCE_SHOTS = 'shots';
export type SegmentSource = typeof SEGMENT_SOURCE_SHOTS;

// ---------------------------------------------------------------- settings (workspace_settings.shot_detection)

/** Ranges of the settings (plan §1). */
export const SHOT_LIMITS = {
  sensitivity: { min: 1, max: 100 },
  minShotSeconds: { min: 0.2, max: 10 },
  keyframeIntervalSeconds: { min: 2, max: 60 },
  maxTiles: { min: 100, max: 600 },
} as const;

export const SHOT_DETECTION_DEFAULTS = {
  detectAtIngest: true,
  sensitivity: 50,
  minShotSeconds: 0.5,
  keyframeIntervalSeconds: 10,
  maxTiles: 300,
} as const;

const sensitivity = z.number().int().min(SHOT_LIMITS.sensitivity.min).max(SHOT_LIMITS.sensitivity.max);
const minShotSeconds = z.number().min(SHOT_LIMITS.minShotSeconds.min).max(SHOT_LIMITS.minShotSeconds.max);
const keyframeIntervalSeconds = z.number().min(SHOT_LIMITS.keyframeIntervalSeconds.min).max(SHOT_LIMITS.keyframeIntervalSeconds.max);
const maxTiles = z.number().int().min(SHOT_LIMITS.maxTiles.min).max(SHOT_LIMITS.maxTiles.max);

/**
 * The workspace's shot detection settings (Administration → Transcoding, "Shots and scrub images"; admin, audited
 * settings.shot_detection). Stored as workspace_settings.shot_detection (0098); a missing key reads as its default.
 */
export const ShotDetectionSettingsSchema = z.object({
  /** Detect shots at ingest (the sprite step). Off: the timed sprite as before 1.5.0, and no Shots layer. */
  detectAtIngest: z.boolean().default(SHOT_DETECTION_DEFAULTS.detectAtIngest),
  /** 1–100, higher finds more cuts (sceneThreshold maps it to FFmpeg's scene-change threshold). */
  sensitivity: sensitivity.default(SHOT_DETECTION_DEFAULTS.sensitivity),
  /** Cuts closer than this to the previous cut are ignored (flashes, strobes). */
  minShotSeconds: minShotSeconds.default(SHOT_DETECTION_DEFAULTS.minShotSeconds),
  /** A forced sprite tile every this many seconds inside a long shot. Tiles only, never shots. */
  keyframeIntervalSeconds: keyframeIntervalSeconds.default(SHOT_DETECTION_DEFAULTS.keyframeIntervalSeconds),
  /** The most scrub images per asset; more shots than this: an even selection (the Shots layer keeps every shot). */
  maxTiles: maxTiles.default(SHOT_DETECTION_DEFAULTS.maxTiles),
});
export type ShotDetectionSettings = z.infer<typeof ShotDetectionSettingsSchema>;
/** GET /api/v1/settings/shot-detection (asset.read: the Detect shots dialog prefills from it). */
export interface ShotDetectionSettingsDTO extends ShotDetectionSettings {
  updatedAt: string | null;
}
/** PUT /api/v1/settings/shot-detection (admin): the whole settings object; missing keys take their defaults. */
export const ShotDetectionSettingsInput = ShotDetectionSettingsSchema;

/** Read stored settings (null, {} or a partial object from an older release): every key, defaults filled in. */
export function shotDetectionSettingsOf(stored: unknown): ShotDetectionSettings {
  const r = ShotDetectionSettingsSchema.safeParse(stored && typeof stored === 'object' ? stored : {});
  return r.success ? r.data : ShotDetectionSettingsSchema.parse({});
}

/**
 * What one detection runs with: the settings without the ingest switch. The derivative job's `shots` (absent: the
 * timed sprite), the Detect shots run's resolved overrides, and what ShotsSummary reports.
 */
export const ShotDetectionParamsSchema = z.object({
  sensitivity, minShotSeconds, keyframeIntervalSeconds, maxTiles,
});
export type ShotDetectionParams = z.infer<typeof ShotDetectionParamsSchema>;

export function shotParamsOf(s: ShotDetectionSettings, overrides: Partial<ShotDetectionParams> = {}): ShotDetectionParams {
  return {
    sensitivity: overrides.sensitivity ?? s.sensitivity,
    minShotSeconds: overrides.minShotSeconds ?? s.minShotSeconds,
    keyframeIntervalSeconds: overrides.keyframeIntervalSeconds ?? s.keyframeIntervalSeconds,
    maxTiles: overrides.maxTiles ?? s.maxTiles,
  };
}

/**
 * Sensitivity (1–100) → FFmpeg's scene-change threshold (the scene score on RGB frames, 0–1; a frame scoring above it
 * is a cut). Higher sensitivity → lower threshold → more cuts. Calibrated in 1.5.0 on the test clips: a geometric
 * curve 0.6·(0.08/0.6)^((s−1)/99), from 0.60 at 1 to 0.08 at 100, about 0.22 at the default 50. Motion inside a shot
 * scores under 0.08 on RGB; a plain colour cut 0.6 or more.
 */
export const SCENE_THRESHOLD_RANGE = { atMin: 0.6, atMax: 0.08 } as const;
export function sceneThreshold(sensitivityValue: number): number {
  const s = Math.min(SHOT_LIMITS.sensitivity.max, Math.max(SHOT_LIMITS.sensitivity.min, Math.round(sensitivityValue)));
  const f = (s - SHOT_LIMITS.sensitivity.min) / (SHOT_LIMITS.sensitivity.max - SHOT_LIMITS.sensitivity.min);
  const { atMin, atMax } = SCENE_THRESHOLD_RANGE;
  return Math.round(atMin * Math.pow(atMax / atMin, f) * 10_000) / 10_000;
}

// ---------------------------------------------------------------- shots from cut times

/** One shot, in ticks of the proxy (zero-based): a Shots layer segment. */
export interface ShotSpan {
  startTicks: number;
  endTicks: number;
}

/**
 * The shot list from the cut times FFmpeg reported (seconds, any order):
 *  - each cut is snapped to the nearest frame (with a frame rate);
 *  - cuts at or before 0, at or after the end, or closer than minShotSeconds to the start or the end are dropped;
 *  - a flash: a cluster of cuts each closer than minShotSeconds to the one before, spanning less than
 *    minShotSeconds, is dropped as a whole (into and out of the flash), so the flash makes no shot and no cut;
 *    unless `changed(first, last)` (given the cluster's first and last snapped cuts, in seconds; the caller compares
 *    the frame before the first with the frame of the last) says the picture really changed across it: then it is a
 *    real cut straight into a flash, and the cluster counts as its first cut alone (the edit point);
 *  - a longer cluster (fast cutting, a strobe): the first cut is kept, then each cut at least minShotSeconds after the
 *    last kept one;
 *  - shots run from cut to cut, the first from 0, the last to the duration. No cut (or no duration): one shot.
 * Every shot is at least minShotSeconds long, unless the file is shorter.
 */
export function shotsFromCuts(
  cuts: number[],
  opts: { minShotSeconds: number; durationSeconds: number; frameRate: Rational | null; changed?: (firstCutSeconds: number, lastCutSeconds: number) => boolean },
): ShotSpan[] {
  const endTicks = Math.max(0, secondsToTicks(opts.durationSeconds));
  if (endTicks <= 0) return [{ startTicks: 0, endTicks: 0 }];
  const minTicks = Math.max(0, secondsToTicks(opts.minShotSeconds));
  const rate = opts.frameRate && opts.frameRate.num > 0 && opts.frameRate.den > 0 ? opts.frameRate : null;
  const fps = rate ? rationalToNumber(rate) : 0;
  const snap = (s: number) => (rate ? framesToTicks(Math.round(s * fps), rate) : secondsToTicks(s));
  const snapped = [...new Set(cuts.filter((c) => Number.isFinite(c)).map(snap))].sort((a, b) => a - b).filter((t) => t > 0 && t < endTicks);

  // Clusters of cuts closer than the minimum to the one before.
  const clusters: number[][] = [];
  for (const t of snapped) {
    const last = clusters[clusters.length - 1];
    if (last && t - last[last.length - 1]! < minTicks) last.push(t);
    else clusters.push([t]);
  }
  const kept: number[] = [];
  for (const cluster of clusters) {
    let c = cluster;
    if (c.length > 1 && c[c.length - 1]! - c[0]! < minTicks) {
      // A flash, unless the picture before and after it differs (a real cut straight into a flash).
      if (!opts.changed?.(ticksToSeconds(c[0]!), ticksToSeconds(c[c.length - 1]!))) continue;
      c = [c[0]!];
    }
    for (const t of c) {
      const prev = kept.length ? kept[kept.length - 1]! : 0;
      if (t - prev < minTicks) continue;
      if (endTicks - t < minTicks) continue;
      kept.push(t);
    }
  }
  const bounds = [0, ...kept, endTicks];
  const shots: ShotSpan[] = [];
  for (let i = 0; i < bounds.length - 1; i++) shots.push({ startTicks: bounds[i]!, endTicks: bounds[i + 1]! });
  return shots;
}

// ---------------------------------------------------------------- sprite tile times

/**
 * When each sprite tile is taken (seconds of the proxy), and whether it is forced (inside a long shot) rather than a
 * shot's start. Every shot's start, plus one tile every keyframeIntervalSeconds after the start inside a longer shot
 * (before its end). Over maxTiles: the shot starts are kept first (thinned evenly, the first and last kept, when there
 * are more of them than maxTiles), the forced tiles fill what is left, thinned evenly. Sorted by time, never empty.
 */
export function spriteTileTimes(shots: ShotSpan[], opts: { keyframeIntervalSeconds: number; maxTiles: number }): { seconds: number[]; forced: boolean[] } {
  const cap = Math.max(1, Math.floor(opts.maxTiles));
  const interval = Math.max(0.001, opts.keyframeIntervalSeconds);
  const sorted = [...shots].sort((a, b) => a.startTicks - b.startTicks);
  if (!sorted.length) return { seconds: [0], forced: [false] };
  const starts = sorted.map((s) => s.startTicks / TICKS_PER_SECOND);
  const forcedAll: number[] = [];
  for (const s of sorted) {
    const a = s.startTicks / TICKS_PER_SECOND;
    const b = s.endTicks / TICKS_PER_SECOND;
    for (let k = 1; ; k++) {
      const t = a + k * interval;
      if (t >= b - 1e-6) break;
      forcedAll.push(Math.round(t * 1e6) / 1e6);
    }
  }
  const keepStarts = starts.length > cap ? evenWithEnds(starts, cap) : starts;
  const room = cap - keepStarts.length;
  const keepForced = room <= 0 ? [] : forcedAll.length > room ? evenCentred(forcedAll, room) : forcedAll;
  const tiles = [...keepStarts.map((t) => ({ t, forced: false })), ...keepForced.map((t) => ({ t, forced: true }))]
    .sort((x, y) => x.t - y.t || Number(x.forced) - Number(y.forced));
  const out: { seconds: number[]; forced: boolean[] } = { seconds: [], forced: [] };
  for (const x of tiles) {
    if (out.seconds.length && Math.abs(out.seconds[out.seconds.length - 1]! - x.t) < 1e-6) continue;
    out.seconds.push(x.t);
    out.forced.push(x.forced);
  }
  return out;
}

/** k of the items, evenly, the first and the last included. */
function evenWithEnds<T>(items: T[], k: number): T[] {
  if (k >= items.length) return items;
  if (k === 1) return [items[0]!];
  return Array.from({ length: k }, (_, i) => items[Math.round((i * (items.length - 1)) / (k - 1))]!);
}
/** k of the items, evenly, each from the middle of its share. */
function evenCentred<T>(items: T[], k: number): T[] {
  if (k >= items.length) return items;
  return Array.from({ length: k }, (_, i) => items[Math.min(items.length - 1, Math.floor(((i + 0.5) * items.length) / k))]!);
}

/**
 * The sprite tile to show at a time (seconds): the latest tile taken at or before it (a binary search of
 * layout.tileSeconds, 1.5.0), or for a timed sprite (no tileSeconds) the uniform interval's tile. Always a valid
 * index (0 … count − 1). The Library card's hover maps the pointer's fraction of the duration to a time first.
 */
export function spriteTileAt(layout: SpriteLayout, seconds: number): number {
  const last = Math.max(0, layout.count - 1);
  const t = Number.isFinite(seconds) ? seconds : 0;
  const times = layout.tileSeconds;
  if (times && times.length) {
    if (t < times[0]!) return 0;
    let lo = 0;
    let hi = Math.min(times.length, layout.count) - 1;
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      if (times[mid]! <= t) lo = mid;
      else hi = mid - 1;
    }
    return Math.min(lo, last);
  }
  const interval = layout.intervalSeconds > 0 ? layout.intervalSeconds : 1;
  return Math.max(0, Math.min(last, Math.floor(t / interval + 1e-9)));
}

// ---------------------------------------------------------------- the sprite's attributes and the asset's summary

/**
 * What the sprite derivative reports about detection, in its attributes under SPRITE_SHOTS_ATTRIBUTE (packages/media
 * writes it; record.ts reads it when the sprite is recorded, writes the Shots layer and stores only the summary, not
 * the list). `failed`: detection failed and the sprite is the timed one (the step says "shots: not detected (reason)").
 */
export const SPRITE_SHOTS_ATTRIBUTE = 'shotDetection';
export type SpriteShotDetection =
  | { status: 'detected'; shots: ShotSpan[]; params: ShotDetectionParams; threshold: number }
  | { status: 'failed'; reason: string; params: ShotDetectionParams };

/** AssetDetail.shots: "42 shots · detected with sensitivity 50". Null: never detected (no Shots layer yet). */
export interface ShotsSummary {
  count: number;
  detectedAt: string;
  settings: Pick<ShotDetectionParams, 'sensitivity' | 'minShotSeconds' | 'keyframeIntervalSeconds'>;
}

// ---------------------------------------------------------------- Detect shots (plan §4)

/** Overrides of POST /api/v1/assets/:id/detect-shots (processing.retry); missing ones: the workspace's settings. */
export const DetectShotsRequest = z.object({
  sensitivity: sensitivity.optional(),
  minShotSeconds: minShotSeconds.optional(),
  keyframeIntervalSeconds: keyframeIntervalSeconds.optional(),
}).strict();
export type DetectShotsRequest = z.infer<typeof DetectShotsRequest>;
/** Its answer (202): the run started (processing_runs kind 'shots', its one step 'sprite'). */
export interface DetectShotsResult {
  runId: string;
  workflowId: string;
  versionId: string;
  params: ShotDetectionParams;
}
/** The detectShots workflow's id: one per version at a time (a later run reuses it once the earlier one closed). */
export const detectShotsWorkflowId = (versionId: string) => `shots-${versionId}`;
/** processing_runs.kind of a Detect shots run. */
export const SHOTS_RUN_KIND = 'shots';

// ---------------------------------------------------------------- Fill from shots, Remove empty (plan §5)

/** POST /api/v1/assets/:id/segments/from-shots (asset.edit_metadata): a user layer, the whole asset or an In–Out range. */
export const SegmentsFromShotsRequest = z.object({
  stratumId: z.string().regex(/^s_[a-z0-9_]{1,40}$/),
  startTicks: z.number().int().nonnegative().optional(),
  endTicks: z.number().int().positive().optional(),
}).strict();
export type SegmentsFromShotsRequest = z.infer<typeof SegmentsFromShotsRequest>;
/** "38 added, 4 skipped (already logged)". */
export interface SegmentsFromShotsResult {
  added: number;
  skipped: number;
  /** Shots in the range. */
  shots: number;
}
/** POST /api/v1/assets/:id/segments/remove-empty (asset.edit_metadata): that layer's segments with no text and no values. */
export const RemoveEmptySegmentsRequest = z.object({ stratumId: z.string().regex(/^s_[a-z0-9_]{1,40}$/) }).strict();
export type RemoveEmptySegmentsRequest = z.infer<typeof RemoveEmptySegmentsRequest>;
export interface RemoveEmptySegmentsResult {
  removed: number;
}

// ---------------------------------------------------------------- errors

export const SHOT_ERRORS = {
  /** 409: Detect shots while an ingest (or another Detect shots) run of the version is active. */
  PROCESSING_ACTIVE: 'PROCESSING_ACTIVE',
  /** 409: writing to a system layer (QC, Shots); the code strata.ts already answers. */
  SYSTEM_LAYER: 'SYSTEM_LAYER',
  /** 409: Detect shots without a proxy (proxy:false, still recording, audio-only). */
  NO_PROXY: 'NO_PROXY',
  /** 409: Fill from shots before shots were detected. */
  NO_SHOTS: 'NO_SHOTS',
  /** 422: Detect shots on an audio, image or graphics asset. */
  NOT_VIDEO: 'NOT_VIDEO',
} as const;
export type ShotErrorCode = keyof typeof SHOT_ERRORS;
export const SHOT_ERROR_STATUS: Record<ShotErrorCode, number> = {
  PROCESSING_ACTIVE: 409,
  SYSTEM_LAYER: 409,
  NO_PROXY: 409,
  NO_SHOTS: 409,
  NOT_VIDEO: 422,
};
